
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ChildService } from "@/lib/services/child-service";
import { ChildInsert } from "@/lib/repositories/child.repository";
import { useChildStore } from "@/lib/store/child";
import { useCaregiverStore } from "@/lib/store/caregiver";
import { logger } from "@/lib/services/logger-service";

export function useAddChildForm(onSuccess?: (childId: string) => void, onCancel?: () => void) {
  const [name, setName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<Date>();
  const { toast } = useToast();
  const { caregiverId } = useCaregiverStore();
  const { setChildren } = useChildStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!caregiverId || !name || !dateOfBirth) {
      toast({
        title: "Error",
        description: "Missing required information. Please provide name and date of birth.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Set the time to noon UTC (12:00:00) to prevent date shifting across time zones
      const dateAtNoonUTC = new Date(dateOfBirth);
      dateAtNoonUTC.setUTCHours(12, 0, 0, 0);
      
      // Format as ISO string for consistent database storage
      const formattedDate = dateAtNoonUTC.toISOString();
      
      logger.debug('Birthdate formatted with noon UTC approach', {
        originalDate: dateOfBirth.toISOString(),
        noonUTCDate: formattedDate,
        localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        tzOffset: new Date().getTimezoneOffset()
      });
      
      // Format the child data using the proper ChildInsert type
      const childData: ChildInsert = {
        name,
        date_of_birth: formattedDate,
        caregiver_id: caregiverId
      };

      // Use the service to add the child
      const newChild = await ChildService.addChild(childData);
      
      logger.debug('Child added successfully', { childId: newChild.id });

      // Refresh the children list
      const { data, error } = await supabase
        .from('children')
        .select('id, name, date_of_birth')
        .eq('caregiver_id', caregiverId)
        .order('created_at', { ascending: false });

      if (!error && data) {
        setChildren(data);
      }

      toast({
        title: "Success",
        description: "Child added successfully",
      });

      if (onSuccess) {
        onSuccess(newChild.id);
      }
      resetForm();
    } catch (error) {
      logger.error('Error adding child:', { error });
      toast({
        title: "Error",
        description: "Failed to add child. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setName("");
    setDateOfBirth(undefined);
  };

  return {
    name,
    setName,
    dateOfBirth,
    setDateOfBirth,
    isSubmitting,
    handleSubmit,
    resetForm
  };
}
